import React from "react";
import { Tab, makeStyles } from "@material-ui/core";
import { useLocalStore, useObserver } from "mobx-react";
import TabContext from "@material-ui/lab/TabContext";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import { QualityCheck, QualityCheckProps } from "../quality-check";
import { QualityChecksStore } from "../../quality-checks-store";
import QualityCheckEditor from "./quality-check-editor";
import { Spinner } from "@common";
import locale from "@bonliva-crm/locale";
import { HospQualityCheck } from "../quality-check/hosp-quality-check";
import { ProfessionCertQualityCheck } from "../quality-check/professsion-cert-quality-check";
import { HealthDeclarationQualityCheck } from "../quality-check/health-declaration-quality-check";
import { ReferenceQualityCheck } from "../quality-check/reference-quality-check/reference-quality-check";
import { NationalProcurementReferenceQualityCheck } from "../quality-check/national-procurement-reference-quality-check/national-procurement-reference-quality-check";
import { CriminalRecordQualityCheck } from "../quality-check/criminal-record-quality-check/criminal-record-quality-check";
import { NationalCriminalRecordQualityCheck } from "../quality-check/national-criminal-record-quality-check/national-criminal-record-quality-check";

import { HsaidQualityCheck } from "../quality-check/hsaid-quality-check/hsaid-quality-check";
import {
  HlrQualityCheck,
  hlrNames
} from "../quality-check/hlr-quality-check/hlr-quality-check";
import { IvoQualityCheck, ivoNames } from "../quality-check/ivo-quality-check";
import { HprQualityCheck } from "../quality-check/hpr-quality-check/hpr-quality-check";
import { SakQualityCheck } from "../quality-check/sak-quality-check/sak-quality-check";

type QualityChecksTabProps = {
  consultantId: string;
};

const sortWeights = {
  Sweden: 1,
  Norway: 2,
  "Bonliva Care": 3
};

export function QualityCheckSection({ consultantId }: QualityChecksTabProps) {
  const store = useLocalStore(() => new QualityChecksStore(consultantId));

  const [value, setValue] = React.useState("Sweden");
  const classes = useStyles();

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  return useObserver(() => {
    const { consultant, isLoading } = store;

    if (isLoading) {
      return <Spinner />;
    }

    const tabs =
      consultant.QualityPerCountry.map(({ Country }) => ({
        value: Country,
        label: locale.countries[Country],
        disabled:
          Country === "Bonliva Care" &&
          !consultant.AvailableForWorkInCountries.includes("Bonliva Care")
      })).sort((a, b) => sortWeights[a.value] - sortWeights[b.value]) || [];

    return (
      <TabContext value={value}>
        {store.activeQualityCheck ? <QualityCheckEditor store={store} /> : null}

        <TabList onChange={handleChange} aria-label="simple tabs example">
          {tabs.map((tab, i) => (
            <Tab key={i} label={tab.label} value={tab.value} disabled={tab.disabled} />
          ))}
        </TabList>

        {tabs.map((tab, i) => {
          const qPerCountry = consultant.QualityPerCountry.find(
            ({ Country }) => tab.value === Country
          );

          return (
            <TabPanel value={tab.value} className={classes.tabPanel} key="tab">
              {qPerCountry.QualityChecks.map((check, i) => {
                const qualityCheckProps: QualityCheckProps = {
                  active:
                    store.activeQualityCheck &&
                    check.Country === store.activeQualityCheck.country &&
                    check.Name === store.activeQualityCheck.name,
                  consultantData: store.consultant,
                  data: check,
                  disabled: !store.canEdit || check.Disabled,
                  disabledTitle: check.DisabledMsg,
                  onClick: () =>
                    store.openQualityCheckEditor(
                      check.Country,
                      check.Name,
                      check.DocumentType,
                      check.templateName
                    )
                };

                if (check.Name === "HOSP") {
                  return <HospQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (check.Name === "Yrkesbevis") {
                  return <ProfessionCertQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (check.Name === "Hälsodeklaration") {
                  return <HealthDeclarationQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (check.Name === "Referenser") {
                  return (
                    <ReferenceQualityCheck
                      key={i}
                      notClickable={true}
                      {...qualityCheckProps}
                    />
                  );
                }

                if (check.Name === "Referenser Nationella") {
                  return (
                    <NationalProcurementReferenceQualityCheck
                      key={i}
                      notClickable={true}
                      {...qualityCheckProps}
                    />
                  );
                }

                if (check.Name === "Belastningsregister") {
                  return <CriminalRecordQualityCheck {...qualityCheckProps} />;
                }

                if (check.Name === "Belastningsregister Nationella") {
                  return <NationalCriminalRecordQualityCheck {...qualityCheckProps} />;
                }

                if (check.Name === "HSAID/SITHS") {
                  return <HsaidQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (check.Name === "HPR-nummer") {
                  return <HprQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (check.Name === "Helsetillsynet (SAK)") {
                  return <SakQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (hlrNames.includes(check.Name)) {
                  return <HlrQualityCheck key={i} {...qualityCheckProps} />;
                }

                if (ivoNames.includes(check.Name)) {
                  return <IvoQualityCheck key={i} {...qualityCheckProps} />;
                }

                return <QualityCheck key={i} {...qualityCheckProps} />;
              })}
            </TabPanel>
          );
        })}
      </TabContext>
    );
  });
}

const useStyles = makeStyles((theme) => ({
  tabPanel: {
    marginTop: 18,
    padding: 8,
    boxShadow: "1px 6px 24px 0px #80808026",
    borderRadius: "0.125rem",
    backgroundColor: theme.palette.common.white
  }
}));
